import ReferenceChart from "@/components/ReferenceChart";

const ROWS = [
  // Hydrocarbons
  { key: "alkane", big: "R–H", small: "sp³", name: "Alkane", group: "Hydrocarbon", tag: "-ane", note: "Single C–C bonds. Non-polar, unreactive. Ex: methane, ethane." },
  { key: "alkene", big: "C=C", small: "sp²", name: "Alkene", group: "Hydrocarbon", tag: "-ene", note: "One C=C double bond. Undergoes addition. Cis/trans isomerism." },
  { key: "alkyne", big: "C≡C", small: "sp", name: "Alkyne", group: "Hydrocarbon", tag: "-yne", note: "Triple bond. Terminal alkyne H (pKa~25) is acidic — can be deprotonated." },
  { key: "aromatic", big: "Ar", small: "Ph", name: "Aromatic", group: "Hydrocarbon", tag: "-benzene", note: "Cyclic, planar, 4n+2 π electrons. Undergoes substitution (EAS), not addition." },
  // Oxygen-containing
  { key: "alcohol", big: "R-OH", small: "1°/2°/3°", name: "Alcohol", group: "Oxygen (single-bond)", tag: "-ol", note: "H-bond donor+acceptor. Oxidize 1° → aldehyde → acid, 2° → ketone, 3° = no ox." },
  { key: "ether", big: "R-O-R", small: "-O-", name: "Ether", group: "Oxygen (single-bond)", tag: "-oxy-", note: "H-bond ACCEPTOR only (no O-H). Unreactive, good solvents. Ex: diethyl ether." },
  { key: "phenol", big: "Ph-OH", small: "-OH on Ar", name: "Phenol", group: "Oxygen (single-bond)", tag: "-ol", note: "Aromatic -OH. Acidic (pKa~10) vs alcohols (~16) due to resonance stabilization." },
  { key: "aldehyde", big: "R-CHO", small: "R-C(=O)H", name: "Aldehyde", group: "Carbonyl", essential: true, tag: "-al", note: "C=O at chain END. Oxidizes readily to carboxylic acid. Ex: acetaldehyde." },
  { key: "ketone", big: "R-CO-R", small: "R-C(=O)-R", name: "Ketone", group: "Carbonyl", essential: true, tag: "-one", note: "C=O in MIDDLE of chain. Less reactive than aldehyde (steric+electronic)." },
  // Carboxylic acid family
  { key: "carboxylic", big: "R-COOH", small: "-C(=O)OH", name: "Carboxylic Acid", group: "Acid family", essential: true, tag: "-oic acid", note: "Acidic (pKa~4-5). Resonance stabilizes conjugate base. H-bonds strongly." },
  { key: "ester", big: "R-COOR", small: "-C(=O)O-R", name: "Ester", group: "Acid family", tag: "-oate", note: "From acid + alcohol (Fischer). Pleasant smells. Hydrolyzes back." },
  { key: "amide", big: "R-CONR₂", small: "-C(=O)N-", name: "Amide", group: "Acid family", tag: "-amide", note: "MOST STABLE acid derivative. Peptide bonds are amides. Weak base." },
  { key: "anhydride", big: "R-CO-O-CO-R", small: "(RCO)₂O", name: "Anhydride", group: "Acid family", tag: "-oic anhydride", note: "Very reactive acylating agent. Two acid groups sharing O." },
  { key: "acyl-halide", big: "R-COX", small: "R-C(=O)Cl", name: "Acyl Halide", group: "Acid family", tag: "-oyl chloride", note: "MOST REACTIVE acid derivative. Powerful acylating agents." },
  // Nitrogen
  { key: "amine", big: "R-NH₂", small: "1°/2°/3°", name: "Amine", group: "Nitrogen", essential: true, tag: "-amine", note: "Basic (lone pair). 1°/2° H-bond; 3° can't donate. pKa of conjugate acid ~10." },
  { key: "nitrile", big: "R-C≡N", small: "-CN", name: "Nitrile", group: "Nitrogen", tag: "-nitrile", note: "C≡N. Hydrolyzes to acid. Adds one carbon." },
  { key: "amino-acid", big: "H₂N–CR–COOH", small: "AA", name: "Amino Acid", group: "Nitrogen", tag: "α-amino acid", note: "Zwitterion at physiological pH. Both -NH₃⁺ and -COO⁻ present." },
  // Sulfur / Halide
  { key: "thiol", big: "R-SH", small: "-SH", name: "Thiol", group: "Sulfur/Halide", tag: "-thiol", note: "Sulfur alcohol. Forms disulfide (–S–S–) bonds. Cysteine in proteins." },
  { key: "halide", big: "R-X", small: "-Cl/Br/I", name: "Alkyl Halide", group: "Sulfur/Halide", tag: "-halo-", note: "SN1/SN2/E1/E2 substrate. Leaving group ability: I > Br > Cl > F." },
];

const GROUP_COLORS = {
  Hydrocarbon: { bg: "#f3ede1", text: "#7a5b2a", pill: "#c98a3b" },
  "Oxygen (single-bond)": { bg: "#efe4d8", text: "#6a4a2f", pill: "#a5754a" },
  Carbonyl: { bg: "#e8f1e5", text: "#3d5a2f", pill: "#7ba55c" },
  "Acid family": { bg: "#e5edf5", text: "#2d4a6f", pill: "#4f7d8a" },
  Nitrogen: { bg: "#f6e5e5", text: "#7a3a3a", pill: "#c95e5e" },
  "Sulfur/Halide": { bg: "#efe4f0", text: "#5c3a5c", pill: "#8b5c8b" },
};

export default function FunctionalGroupsChart() {
  return (
    <ReferenceChart
      title="Organic Functional Groups"
      subtitle="Grouped by chemistry. The Acid Family row is ordered by reactivity — acyl halide > anhydride > ester > amide."
      sourceChart="functional-groups"
      rows={ROWS}
      groupColors={GROUP_COLORS}
      deckMapper={(r) => ({
        front: `${r.name} (${r.big})`,
        back: `${r.group}. Suffix: ${r.tag}. ${r.note}`,
        meta: { key: r.key, group: r.group },
      })}
      footer={
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <b>Acid derivative reactivity (memorize):</b><br />
            Acyl halide &gt; Anhydride &gt; Ester &gt; Amide<br />
            (better leaving group = more reactive).
          </div>
          <div>
            <b>IR fingerprints:</b> O-H (broad ~3200-3550) · N-H (sharp
            ~3300) · C=O (~1700, higher for acid halides) · C≡N (~2250) ·
            C≡C (~2100-2260).
          </div>
        </div>
      }
    />
  );
}
